import React from 'react';
import SectionHeader from '../components/SectionHeader';
import {
  btnPrimary,
  demoType,
  FERTILITY_DEMO_SECTION_BORDER,
  introCardKeyline,
  introHeadlineSerifDeep,
  linkInline,
  demoChapterStackColumnStyle,
} from '../designSystem';

const homeGridDescriptorEllipsis = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const homeGridLinkStyle = {
  fontSize: 12,
  color: 'var(--pink, #d4537e)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

function CardSectionHeader({ title, descriptor, trailing, homeGridCard, linkStyle = homeGridLinkStyle }) {
  const wrapTrailing = (node) => {
    if (!node) return null;
    if (React.isValidElement(node)) {
      return React.cloneElement(node, {
        style: { ...node.props.style, ...linkStyle, marginLeft: 0 },
      });
    }
    return node;
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: homeGridCard ? 2 : 12,
        gap: 10,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        <SectionHeader title={title} descriptor={descriptor} descriptorStyle={homeGridCard ? homeGridDescriptorEllipsis : undefined} />
      </div>
      {trailing ? <div style={{ flexShrink: 0, marginLeft: homeGridCard ? 12 : 0 }}>{wrapTrailing(trailing)}</div> : null}
    </div>
  );
}

const getTimeOfDay = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return 'morning';
  if (h >= 12 && h < 17) return 'afternoon';
  if (h >= 17 && h < 21) return 'evening';
  return 'night';
};

const timeLabels = {
  morning: 'Good morning',
  afternoon: 'Good afternoon',
  evening: 'Good evening',
  night: 'Tonight',
};

export default function FertilityDemoHome({ palette, m, navigate, omitHeader = false }) {
  const dt = demoType(m);
  const gridLink = { ...homeGridLinkStyle, fontSize: dt.link };
  const timeOfDay = getTimeOfDay();
  const greeting = timeLabels[timeOfDay];
  const cardStyle = {
    background: 'var(--white)',
    border: FERTILITY_DEMO_SECTION_BORDER,
    borderRadius: 14,
    padding: '14px 16px',
  };

  const currentCycle = {
    number: 2,
    stimDay: 8,
    estimatedStimDays: 14,
    daysToRetrieval: 9,
    estimatedRetrieval: 'Mar 25',
    protocol: 'Antagonist',
    clinic: 'Ohio Fertility Network',
    doctor: 'Dr. Patel',
    stage: 'Stimming',
    follicles: 6,
    medications: [
      { name: 'Gonal-F 150IU', time: '9pm' },
      { name: 'Menopur 75IU', time: '9pm' },
      { name: 'Cetrotide 0.25mg', time: '8am' },
    ],
    step: 4,
    totalSteps: 7,
    stepName: 'Retrieval',
  };
  const ivfSteps = ['Consult', 'Testing', 'Stims', 'Retrieval', 'Fert report', 'Transfer', '2WW'];
  const userName = 'Maya';

  const hasActiveCycle = currentCycle !== null && currentCycle !== undefined;
  const hasFullData = hasActiveCycle && currentCycle.stimDay && currentCycle.stimDay > 0;
  const state1 = !hasActiveCycle;
  const state2 = hasActiveCycle && !hasFullData;
  const state3 = hasActiveCycle && hasFullData;

  const heroCardStyle = { background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))', ...introCardKeyline, padding: m ? '20px 22px' : '22px 26px' };
  const topLabelStyle = { fontSize: dt.label, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#993556', marginBottom: 5 };
  const heroTextStyle = { ...introHeadlineSerifDeep, fontSize: dt.h2card };
  const sublineStyle = { fontSize: dt.bodySm, color: 'rgba(60,30,45,0.55)', marginBottom: 14 };
  const linkInlineTouch = { ...linkInline, fontSize: dt.micro };
  const emptyBox = (title, sub, cta) => (
    <div style={{ background: 'var(--color-background-secondary, #f8f8f8)', border: '0.5px dashed rgba(212,83,126,0.25)', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
      <div style={{ fontSize: dt.small, fontWeight: 500, color: '#2d1a22', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: dt.micro, color: 'var(--color-text-tertiary, #888)', marginBottom: 8 }}>{sub}</div>
      <span onClick={() => navigate('/signup')} role="button" tabIndex={0} style={linkInlineTouch}>{cta}</span>
    </div>
  );

  const heroTextByTime = { morning: `Welcome, ${userName}. Nova is ready.`, afternoon: `Hi ${userName} — glad you're here.`, evening: `Good evening, ${userName}.`, night: `No rush, ${userName} — Nova is here.` };
  const sublineByTime1 = {
    morning: "Start by telling Nova about your journey so far — where you are, what you've tried, and what's coming next.",
    afternoon: "Nova is waiting to learn about your journey. It only takes a few minutes and she'll personalize everything from there.",
    evening: "When you're ready, tell Nova where you are in your journey. She'll check in at exactly the right moments.",
    night: "When you're ready tomorrow, share where you are and Nova will start personalizing your experience.",
  };

  const womenLikeYouCard = (
    <div style={cardStyle}>
      <CardSectionHeader
        linkStyle={gridLink}
        homeGridCard
        title="Women like you"
        descriptor="Matched to your cycle and stage."
        trailing={
          <span onClick={() => navigate('/connect')} role="button" tabIndex={0} style={{ ...linkInline, textTransform: 'none', letterSpacing: 0 }}>
            See all →
          </span>
        }
      />
      <div>
        {[
          { initials: 'JM', name: 'Jasmine M.', sub: 'Cycle 2 · Columbus OH · retrieval same week', last: false },
          { initials: 'NR', name: 'Noelle R.', sub: 'Stims day 8 · Cleveland · low AMH', last: false },
          { initials: 'EV', name: 'Elena V.', sub: 'TWW · Cincinnati · first transfer', last: true },
        ].map((w) => (
          <div
            key={w.name}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: w.last ? '9px 0 0' : '9px 0',
              borderBottom: w.last ? 'none' : '0.5px solid var(--sand-2, #ede8df)',
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'var(--pink-l)',
                color: 'var(--pink-d)',
                fontSize: dt.small,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {w.initials}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: dt.bodySm, fontWeight: 600, color: 'var(--text)' }}>{w.name}</div>
              <div style={{ fontSize: dt.caption, color: 'var(--muted)' }}>{w.sub}</div>
            </div>
            <span
              onClick={() => navigate('/connect')}
              role="button"
              tabIndex={0}
              style={{
                marginLeft: 'auto',
                fontSize: dt.caption,
                color: 'var(--pink)',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }}
            >
              Connect →
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const liveRoomsCard = (
    <div style={cardStyle}>
      <CardSectionHeader
        linkStyle={gridLink}
        homeGridCard
        title="Live rooms"
        descriptor="Support happening right now."
        trailing={
          <span onClick={() => navigate('/community')} role="button" tabIndex={0} style={{ ...linkInline, textTransform: 'none', letterSpacing: 0 }}>
            See all →
          </span>
        }
      />
      <div>
        {[
          {
            name: 'Stims support',
            desc: 'Day 6–10 · injection tips · OHSS watch',
            right: 'live',
            count: '7 online',
            last: false,
          },
          {
            name: 'Retrieval recovery',
            desc: 'Post-retrieval · embryo updates · waiting',
            right: 'live',
            count: '6 online',
            last: false,
          },
          {
            name: 'IVF anxiety — day before',
            desc: 'Facilitated · Columbus area',
            right: 'scheduled',
            count: 'Tomorrow 8pm',
            last: true,
          },
        ].map((room) => (
          <div
            key={room.name}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              padding: room.last ? '9px 0 0' : '9px 0',
              borderBottom: room.last ? 'none' : '0.5px solid var(--sand-2, #ede8df)',
            }}
          >
            <div style={{ minWidth: 0, paddingRight: 8 }}>
              <div style={{ fontSize: dt.bodySm, fontWeight: 500, color: 'var(--text)' }}>{room.name}</div>
              <div style={{ fontSize: dt.micro, color: 'var(--muted)', marginTop: 2 }}>{room.desc}</div>
            </div>
            {room.right === 'live' ? (
              <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#1D9E75', marginRight: 4, flexShrink: 0 }} />
                <span style={{ fontSize: dt.caption, fontWeight: 600, color: '#1D9E75' }}>{room.count}</span>
              </div>
            ) : (
              <span style={{ fontSize: dt.caption, color: 'var(--muted)', flexShrink: 0 }}>{room.count}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const womenAndRoomsRow = (
    <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : '1fr 1fr', gap: 14 }}>
      {womenLikeYouCard}
      {liveRoomsCard}
    </div>
  );

  const retrievalThreeCol = (
    <div
      style={{
        marginTop: 14,
        background: 'var(--pink-l, #fbeaf0)',
        borderRadius: 10,
        padding: '10px 14px',
        display: 'grid',
        gridTemplateColumns: m ? '1fr' : '1fr 1fr 1fr',
        gap: 12,
      }}
    >
      {[
        {
          label: 'What happens',
          body: 'Eggs retrieved under light sedation. 20–30 min procedure at Ohio Fertility Network.',
        },
        {
          label: 'What to bring',
          body: 'ID, insurance card, and someone to drive you home. Arrive 30 min early.',
        },
        {
          label: 'What comes next',
          body: 'Fertilization report in 24–48 hrs. Rest that day — no driving after sedation.',
        },
      ].map((col) => (
        <div key={col.label}>
          <div
            style={{
              fontSize: dt.micro,
              fontWeight: 700,
              color: 'var(--pink-d, #993556)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 4,
              fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
            }}
          >
            {col.label}
          </div>
          <div style={{ fontSize: dt.caption, color: '#4b1528', lineHeight: 1.45, fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}>{col.body}</div>
        </div>
      ))}
    </div>
  );

  const state3FourCardStack = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : '1fr 2fr', gap: 14 }}>
        <div style={cardStyle}>
          <CardSectionHeader
            linkStyle={gridLink}
            homeGridCard
            title="Medications"
            descriptor="Tonight's schedule."
            trailing={
              <span onClick={() => navigate('/demo/fertility/medications')} role="button" tabIndex={0} style={{ ...linkInlineTouch, textTransform: 'none', letterSpacing: 0 }}>
                See medications →
              </span>
            }
          />
          {currentCycle.medications.map((med, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: dt.caption,
                padding: '3px 0',
                borderBottom: i < currentCycle.medications.length - 1 ? '0.5px solid var(--color-border-tertiary, #e5e5e5)' : 'none',
              }}
            >
              <span style={{ fontWeight: 500, color: '#2d1a22' }}>{med.name}</span>
              <span style={{ color: 'var(--color-text-tertiary, #888)' }}>{med.time}</span>
            </div>
          ))}
          <div
            style={{
              marginTop: 10,
              paddingTop: 10,
              borderTop: '0.5px solid var(--border)',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                fontSize: dt.micro,
                fontWeight: 600,
                padding: '3px 10px',
                borderRadius: 10,
                background: 'var(--pink-l, #fbeaf0)',
                color: 'var(--pink-d, #993556)',
              }}
            >
              Reminder set · 9pm
            </span>
          </div>
        </div>
        <div style={cardStyle}>
          <CardSectionHeader
            linkStyle={gridLink}
            homeGridCard
            title={`${currentCycle.stepName} · step ${currentCycle.step} of ${currentCycle.totalSteps}`}
            descriptor="~Mar 25 · Ohio Fertility Network · Columbus, OH · 6 days away"
            trailing={
              <span onClick={() => navigate('/demo/fertility/myjourney')} role="button" tabIndex={0} style={{ ...linkInlineTouch, textTransform: 'none', letterSpacing: 0 }}>
                See journey →
              </span>
            }
          />
          {/* IVF timeline — round steps + connector line (matches /demo/fertility/myjourney milestones) */}
          <div
            style={{
              display: 'flex',
              position: 'relative',
              marginBottom: 8,
              overflowX: 'auto',
              paddingBottom: 2,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 18,
                left: 18,
                right: 18,
                height: 2,
                background: 'rgba(212,83,126,0.15)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: 18,
                left: 18,
                height: 2,
                background: '#D4537E',
                width: `${((currentCycle.step - 1) / Math.max(1, ivfSteps.length - 1)) * 100}%`,
                transition: 'width 0.5s',
              }}
            />
            {ivfSteps.map((label, idx) => {
              const stepNum = idx + 1;
              const done = stepNum < currentCycle.step;
              const active = stepNum === currentCycle.step;
              return (
                <div
                  key={label}
                  title={label}
                  style={{
                    flex: '1 1 0',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    zIndex: 1,
                    minWidth: 72,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: dt.small,
                      fontWeight: 700,
                      background: done ? 'var(--sage-m)' : active ? 'var(--pink-m)' : 'rgba(255,255,255,0.55)',
                      border: done ? '2px solid var(--sage)' : active ? '2px solid var(--pink)' : '2px solid rgba(212,83,126,0.2)',
                      color: done ? '#085041' : active ? 'var(--pink-dk, #993556)' : 'rgba(60,30,45,0.35)',
                      boxShadow: active ? '0 0 0 4px var(--pink-l)' : 'none',
                    }}
                  >
                    {done ? '✓' : active ? '→' : stepNum}
                  </div>
                  <div
                    style={{
                      fontSize: dt.micro,
                      color: active ? 'var(--pink-d)' : done ? 'var(--sage-d)' : 'rgba(60,30,45,0.35)',
                      fontWeight: active ? 700 : 400,
                      textAlign: 'center',
                      lineHeight: 1.3,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }}
                  >
                    {label}
                  </div>
                </div>
              );
            })}
          </div>
          {retrievalThreeCol}
        </div>
      </div>
      {womenAndRoomsRow}
    </div>
  );

  const communityFeed = (
    <div style={{ ...cardStyle, padding: '14px 18px' }}>
      <CardSectionHeader
        linkStyle={gridLink}
        title="Community"
        descriptor="Posts from people on a similar timeline — your fertility chapter, unfiltered."
        trailing={
          <span onClick={() => navigate('/community')} role="button" tabIndex={0} style={{ ...linkInlineTouch, textTransform: 'none', letterSpacing: 0, fontSize: dt.small, fontWeight: 500 }}>
            See all →
          </span>
        }
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[
          { name: 'Sarah K.', initials: 'S', text: 'Retrieval done! 12 eggs retrieved 🎉', time: '2h ago', hearts: 24, sameBoat: 3, replies: 2, avatarBg: '#E6F1FB', avatarColor: '#185FA5' },
          { name: 'Maya T.', initials: 'M', text: 'Anyone else doing stim day 8 scans tomorrow? Nervous but ready.', time: '5h ago', hearts: 12, sameBoat: 2, replies: 1, avatarBg: '#FBEAF0', avatarColor: '#993556' },
        ].map((p) => (
          <div key={p.name} style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 12, padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: p.avatarBg, color: p.avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: dt.caption, fontWeight: 600, flexShrink: 0 }}>
              {p.initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                <span style={{ fontSize: dt.small, fontWeight: 700, color: '#d4537e' }}>{p.name}</span>
                <span style={{ fontSize: dt.micro, color: 'rgba(60,30,45,0.38)' }}>{p.time}</span>
              </div>
              <div style={{ fontSize: dt.small, color: '#2d1a22', lineHeight: 1.45 }}>{p.text}</div>
              <div style={{ display: 'flex', gap: 10, marginTop: 6, fontSize: dt.micro, color: 'var(--color-text-tertiary)' }}>
                <span>❤️ {p.hearts}</span>
                <span>🤝 {p.sameBoat}</span>
                <span>💬 {p.replies}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ width: '100%', boxSizing: 'border-box', ...demoChapterStackColumnStyle }}>
      {state1 && (
        <>
          {!omitHeader && (
            <>
              <div style={heroCardStyle}>
                <div style={topLabelStyle}>{greeting} · Fertility chapter</div>
                <div style={heroTextStyle}>{heroTextByTime[timeOfDay]}</div>
                <div style={sublineStyle}>{sublineByTime1[timeOfDay]}</div>
                <button
                  type="button"
                  onClick={() => navigate('/nova')}
                  style={{
                    ...btnPrimary,
                    ...(m ? { fontSize: 15, padding: '12px 22px', minHeight: 44 } : {}),
                  }}
                >
                  Start free with Nova
                </button>
              </div>
            </>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : '1fr 1fr', gap: 10 }}>
            <div style={cardStyle}>
              <CardSectionHeader linkStyle={gridLink} title="Your cycle" descriptor="Stim day, follicles, and retrieval countdown — once you start logging." />
              {emptyBox('No cycle logged yet', 'Log your cycle to see stim day, follicle count, and retrieval countdown here', 'Start logging →')}
            </div>
            <div style={cardStyle}>
              <CardSectionHeader linkStyle={gridLink} title="Medications" descriptor="Tonight’s doses and your full protocol — with reminders so nothing slips." />
              {emptyBox('No protocol added', 'Add your medications to get reminders at the right times', 'Add protocol →')}
            </div>
          </div>
        </>
      )}
      {state2 && (
        <>
          {!omitHeader && (
            <>
              <div style={heroCardStyle}>
                <div style={topLabelStyle}>{greeting} · IVF · Cycle {currentCycle.number}</div>
                <div style={heroTextStyle}>{timeOfDay === 'morning' && `Your cycle has started, ${userName}.`}{timeOfDay === 'afternoon' && `How are you feeling today, ${userName}?`}{timeOfDay === 'evening' && `Injections tonight, ${userName}?`}{timeOfDay === 'night' && `Rest well, ${userName}.`}</div>
                <div style={sublineStyle}>{timeOfDay === 'morning' && `${currentCycle.protocol} protocol · ${currentCycle.clinic} · Started Mar 19`}{timeOfDay === 'afternoon' && `${currentCycle.protocol} protocol · ${currentCycle.clinic} · Cycle in progress`}{timeOfDay === 'evening' && `${currentCycle.protocol} protocol · ${currentCycle.clinic} · Log your medications so Nova can remind you`}{timeOfDay === 'night' && `${currentCycle.protocol} protocol · ${currentCycle.clinic} · Cycle in progress`}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: dt.caption, background: 'rgba(255,255,255,0.75)', border: '0.5px solid rgba(212,83,126,0.25)', color: '#D4537E', fontWeight: 500 }}>● Cycle in progress</span>
                  {timeOfDay === 'morning' && <><span onClick={() => navigate('/signup')} role="button" tabIndex={0} style={{ padding: '3px 10px', borderRadius: 999, fontSize: dt.caption, background: 'rgba(255,255,255,0.6)', border: '0.5px solid rgba(212,83,126,0.15)', color: 'rgba(60,30,45,0.4)', cursor: 'pointer' }}>+ Add stim day</span><span onClick={() => navigate('/signup')} role="button" tabIndex={0} style={{ padding: '3px 10px', borderRadius: 999, fontSize: dt.caption, background: 'rgba(255,255,255,0.6)', border: '0.5px solid rgba(212,83,126,0.15)', color: 'rgba(60,30,45,0.4)', cursor: 'pointer' }}>+ Add follicle count</span></>}
                  {timeOfDay === 'afternoon' && <span onClick={() => navigate('/signup')} role="button" tabIndex={0} style={{ padding: '3px 10px', borderRadius: 999, fontSize: dt.caption, background: 'rgba(255,255,255,0.6)', border: '0.5px solid rgba(212,83,126,0.15)', color: 'rgba(60,30,45,0.4)', cursor: 'pointer' }}>+ Log today&apos;s update</span>}
                  {timeOfDay === 'evening' && <span onClick={() => navigate('/signup')} role="button" tabIndex={0} style={{ padding: '3px 10px', borderRadius: 999, fontSize: dt.caption, background: 'rgba(255,255,255,0.6)', border: '0.5px solid rgba(212,83,126,0.15)', color: 'rgba(60,30,45,0.4)', cursor: 'pointer' }}>+ Add medications</span>}
                </div>
              </div>
            </>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : '1fr 1fr', gap: 10 }}>
            <div style={cardStyle}>
              <CardSectionHeader linkStyle={gridLink} title="Your cycle" descriptor="Stim day, follicles, and retrieval countdown at a glance." />
              <div style={{ fontSize: dt.bodySm, fontWeight: 500, color: '#2d1a22', marginBottom: 2 }}>Cycle started</div>
              <div style={{ fontSize: dt.caption, color: 'var(--color-text-tertiary, #888)', marginBottom: 8 }}>Mar 19 · {currentCycle.protocol} · {currentCycle.clinic}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <span onClick={() => navigate('/signup')} role="button" tabIndex={0} style={linkInlineTouch}>+ Stim day</span>
                <span onClick={() => navigate('/signup')} role="button" tabIndex={0} style={linkInlineTouch}>+ Follicles</span>
              </div>
            </div>
            <div style={cardStyle}>
              <CardSectionHeader linkStyle={gridLink} title="Medications" descriptor="Tonight’s doses and your full protocol — with reminders so nothing slips." />
              {emptyBox('No protocol added yet', 'Add your medications to get reminders at the right times', 'Add your protocol →')}
            </div>
          </div>
        </>
      )}
      {state3 && (
        <>
          {!omitHeader && (
            <>
              <div style={heroCardStyle}>
                <div style={topLabelStyle}>{greeting} · IVF · Cycle {currentCycle.number}</div>
                <div style={{ ...introHeadlineSerifDeep, fontSize: dt.h2card, marginBottom: 4 }}>Day {currentCycle.stimDay}, {userName}.</div>
                <div style={sublineStyle}>{timeOfDay === 'morning' && `Stim day ${currentCycle.stimDay} of ~${currentCycle.estimatedStimDays} · Retrieval in about ${currentCycle.daysToRetrieval} days · ${currentCycle.protocol} · ${currentCycle.clinic}`}{timeOfDay === 'afternoon' && 'Midday check-in · Scan is tomorrow · Drink plenty of water today'}{timeOfDay === 'evening' && "Injections at 9pm tonight · Scan tomorrow morning · You're doing great"}{timeOfDay === 'night' && `Injections done for tonight · Scan tomorrow · ${currentCycle.follicles} follicles growing`}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: dt.caption, background: 'rgba(255,255,255,0.75)', border: '0.5px solid rgba(212,83,126,0.25)', color: '#D4537E', fontWeight: 500 }}>● {currentCycle.stage}</span>
                  <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: dt.caption, background: 'rgba(255,255,255,0.6)', border: '0.5px solid rgba(212,83,126,0.15)', color: 'rgba(60,30,45,0.5)' }}>{(timeOfDay === 'morning' || timeOfDay === 'afternoon' || timeOfDay === 'night') ? `${currentCycle.follicles} follicles` : 'Injections at 9pm'}</span>
                  {timeOfDay === 'morning' && <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: dt.caption, background: 'rgba(255,255,255,0.6)', border: '0.5px solid rgba(212,83,126,0.12)', color: 'rgba(60,30,45,0.5)' }}>{currentCycle.medications[0].name}</span>}
                  {timeOfDay === 'afternoon' && <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: dt.caption, background: 'rgba(255,255,255,0.6)', border: '0.5px solid rgba(212,83,126,0.12)', color: 'rgba(60,30,45,0.5)' }}>~{currentCycle.daysToRetrieval} days to retrieval</span>}
                  <span onClick={() => navigate('/demo/fertility/journey')} role="button" tabIndex={0} style={{ ...linkInlineTouch, marginLeft: 'auto' }}>My journey →</span>
                </div>
              </div>
            </>
          )}
        </>
      )}
      {state3 && state3FourCardStack}
      {!state3 && womenAndRoomsRow}
      {communityFeed}
    </div>
  );
}
